/**
 * NER-1282: pure theme helpers para la Comanda app (auto / light / dark).
 *
 * Extraido aparte de ThemeContext para poder testear la logica sin React ni
 * imports que arrastren axios (el repo no tiene @testing-library; mismo patron
 * que en admin-panel — NER-1281).
 *
 * Preferencia ternaria persistida: 'light' | 'dark' | 'auto' (default 'auto').
 * El tema RESUELTO (lo que se pinta) siempre es 'light' | 'dark'.
 *
 * UX cocina: la comanda corre en pantallas siempre encendidas. Cuando el SO no
 * puede determinar la preferencia (kiosks viejos sin matchMedia), el fallback
 * es 'dark' para reducir fatiga visual.
 */

// Clave logica nueva — el storage util la prefija como `comanda_theme-preference`.
export const PREFERENCE_KEY = 'theme-preference';

// Clave legacy binaria (light|dark) que escribia la version anterior.
export const LEGACY_KEY = 'comanda-theme';

// Orden del cycle del toggle: light -> dark -> auto -> light.
export const THEME_PREFERENCES = ['light', 'dark', 'auto'];

export const DEFAULT_PREFERENCE = 'auto';

export function isValidPreference(value) {
  return THEME_PREFERENCES.includes(value);
}

/**
 * Siguiente preferencia en el cycle light -> dark -> auto -> light.
 */
export function nextPreference(current) {
  const idx = THEME_PREFERENCES.indexOf(current);
  if (idx === -1) return DEFAULT_PREFERENCE;
  return THEME_PREFERENCES[(idx + 1) % THEME_PREFERENCES.length];
}

/**
 * Resuelve la preferencia a un tema concreto a aplicar.
 * Cuando es 'auto', usa el flag del sistema (prefers-color-scheme: dark).
 */
export function resolveTheme(preference, systemPrefersDark) {
  if (preference === 'dark') return 'dark';
  if (preference === 'light') return 'light';
  return systemPrefersDark ? 'dark' : 'light';
}

/**
 * Aplica el tema resuelto a un elemento root, respetando el contrato de la
 * comanda app: AMBAS clases viven en <html>. Agrega una y quita la otra.
 */
export function applyTheme(root, resolvedTheme) {
  if (!root || !root.classList) return;
  if (resolvedTheme === 'dark') {
    root.classList.add('dark');
    root.classList.remove('light');
  } else {
    root.classList.add('light');
    root.classList.remove('dark');
  }
}

/**
 * Lee la preferencia del sistema (prefers-color-scheme: dark).
 * Fallback a `true` (dark) cuando matchMedia no esta disponible — kiosks de
 * cocina viejos: preferimos oscuro para reducir fatiga visual.
 */
export function getSystemPrefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Decide la preferencia inicial a partir de lo guardado.
 * - Si la clave nueva es valida, gana.
 * - Si no, migra desde la clave legacy binaria (light|dark).
 * - Si nada aplica, default 'auto'.
 * Retorna { preference, migratedFromLegacy } sin tocar el storage (puro).
 */
export function resolveInitialPreference(savedPreference, legacyValue) {
  if (isValidPreference(savedPreference)) {
    return { preference: savedPreference, migratedFromLegacy: false };
  }
  if (legacyValue === 'light' || legacyValue === 'dark') {
    return { preference: legacyValue, migratedFromLegacy: true };
  }
  return { preference: DEFAULT_PREFERENCE, migratedFromLegacy: false };
}
